import { useToggle } from '../UNSAFE_useToggle';
import { useRef, useCallback } from 'preact/hooks';

export type ActiveOptions = {
  isDisabled?: boolean;
};

/**
 * Indicates if primary mouse button is down
 * @param e
 * @returns
 */
const isPrimaryMouseButtonDown = (e: MouseEvent) => {
  const flags = e.buttons !== undefined ? e.buttons : e.which;
  const primaryMouseButtonDown = (flags & 1) === 1;
  if (primaryMouseButtonDown) {
    return true;
  }
  return false;
};

/**
 * Returns properties to manage active state indication
 * @returns
 */
export function useActive(settings: ActiveOptions = { isDisabled: false }): {
  isActive: boolean;
  activeProps: Record<string, any>;
} {
  const { bool, setTrue, setFalse } = useToggle(false);
  const wasActive = useRef(false);

  const mouseDownTarget = useCallback(
    (e: MouseEvent) => {
      if (isPrimaryMouseButtonDown(e)) {
        setTrue();
      }
    },
    [setTrue]
  );

  const leftTarget = useCallback(
    (e: MouseEvent) => {
      if (isPrimaryMouseButtonDown(e)) {
        wasActive.current = true;
      }
      setFalse();
    },
    [setFalse]
  );

  const enteredTarget = useCallback(
    (e: MouseEvent) => {
      if (wasActive.current) {
        if (isPrimaryMouseButtonDown(e)) {
          setTrue();
        }
        wasActive.current = false;
      }
    },
    [setTrue]
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === ' ' || event.key === 'Enter') {
        setTrue();
      }
    },
    [setTrue]
  );

  const onKeyUp = useCallback(() => {
    setFalse();
  }, [setFalse]);

  const activeProps = settings.isDisabled
    ? {}
    : {
        onMouseDown: mouseDownTarget,
        onMouseLeave: leftTarget,
        onMouseEnter: enteredTarget,
        onMouseUp: setFalse,
        onTouchStart: setTrue,
        onTouchEnd: setFalse,
        onTouchCancel: setFalse,
        onKeyDown,
        onKeyUp
      };

  return {
    isActive: settings.isDisabled ? false : bool,
    activeProps: activeProps
  };
}
